import React, { useEffect, useState } from 'react'
import { Modal, Button, Checkbox, Table } from 'semantic-ui-react'
import { TemplateUserService } from 'utility/services/template-user'
import { LoaderBar } from 'components'
import styled from 'styled-components'
import { useAuth } from 'context'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;

    .ui.table {
        border: none;
        margin-top: 0;
    }
`

export const InviteTemplateAssignModal = ({
    open,
    onClose,
}) => {
    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const { user } = useAuth()

    useEffect(() => {
        if (open && user?.unique_id) {
            fetchTemplates(user.unique_id)
        }
    }, [open, user?.unique_id])

    const fetchTemplates = async userId => {
        try {
            setLoading(true)
            const response = await TemplateUserService.getByUserId(userId)
            setTemplates(response)
            // Check if all templates are selected
            const allSelected = response.every(template => template.IsSelected)
            setSelectAll(allSelected)
        } catch (error) {
            console.error('Error fetching templates:', error)
            setTemplates([])
        } finally {
            setLoading(false)
        }
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll
        setSelectAll(newSelectAll)
        setTemplates(
            templates.map(template => ({
                ...template,
                IsSelected: newSelectAll,
            })),
        )
    }

    const handleTemplateSelect = templateId => {
        const updatedTemplates = templates.map(template =>
            template.TemplateId === templateId
                ? { ...template, IsSelected: !template.IsSelected }
                : template,
        )
        setTemplates(updatedTemplates)
        setSelectAll(updatedTemplates.every(template => template.IsSelected))
    }

    const handleSave = async () => {
        try {
            setLoading(true)
            const selectedTemplateIds = templates
                .filter(template => template.IsSelected)
                .map(template => template.TemplateId)
                .join(';')

            onClose(true, selectedTemplateIds.split(';'))
        } catch (error) {
            console.error('Error saving template assignments:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={() => onClose(false)} size="small">
            <Modal.Header>Assign Templates</Modal.Header>
            <ScrollableContent>
                {loading && <LoaderBar content="Loading" />}
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label="Select All"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>Template Name</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {templates && templates.length > 0 ? (
                            templates.map(template => (
                                <Table.Row key={template.TemplateId}>
                                    <Table.Cell collapsing>
                                        <Checkbox
                                            checked={template.IsSelected}
                                            onChange={() =>
                                                handleTemplateSelect(
                                                    template.TemplateId,
                                                )
                                            }
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        {template.TemplateName ||
                                            'Unnamed Template'}
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan="2" textAlign="center">
                                    No templates available
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </ScrollableContent>
            <Modal.Actions>
                <Button onClick={() => onClose(false)}>Cancel</Button>
                <Button primary onClick={handleSave} loading={loading}>
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    )
}
